import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { CheckCircle2, Loader2, Send } from 'lucide-react';
import { api } from '../../../lib/api';
import { TEMA_DEFECTO, varsTema } from '../../../lib/formularios';
import { useStore } from '../../../store/useStore';
import FormResponder from '../../../pages/FormResponder';
import { mensajeDe } from '../responder/utilidades';
import { fechaHora } from './constantes';
import '../forms.css';
import '../responder/responder.css';
import './buzon.css';

/*
 * Página pública de seguimiento de un caso: `/f/<token>?seguimiento=<t>` (el enlace que recibe quien
 * lo envió en la confirmación y en el acuse por correo). Enseña el folio, el estado y la conversación
 * con el equipo —nunca las notas internas ni quién lo atiende: eso lo filtra el servidor— y deja
 * escribir otra vez (si el caso estaba resuelto, se reabre).
 *
 * `RutaFormularioPublico` decide qué pinta `/f/<token>`: esta página o el formulario para responder.
 */

export function RutaFormularioPublico() {
  const loc = useLocation();
  const t = new URLSearchParams(loc.search).get('seguimiento');
  return t ? <SeguimientoCaso t={t} /> : <FormResponder modo="publico" />;
}

export default function SeguimientoCaso({ t }) {
  const { token } = useParams();
  const oscuro = useStore((s) => s.theme) === 'dark';
  const [estado, setEstado] = useState({ cargando: true, datos: null, error: null });
  const [texto, setTexto] = useState('');
  const [trampa, setTrampa] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [aviso, setAviso] = useState(null);

  useEffect(() => {
    let vivo = true;
    api.get(`/forms/publico/${token}/seguimiento`, { params: { t }, skipAuthRedirect: true })
      .then(({ data }) => vivo && setEstado({ cargando: false, datos: data, error: null }))
      .catch((err) => vivo && setEstado({ cargando: false, datos: null, error: mensajeDe(err, 'No encontramos ese caso.') }));
    return () => { vivo = false; };
  }, [token, t]);

  useEffect(() => {
    if (estado.datos) document.title = `Caso ${estado.datos.folio} · ${estado.datos.titulo}`;
  }, [estado.datos]);

  const vars = useMemo(() => varsTema(estado.datos?.tema || TEMA_DEFECTO, { oscuro }), [estado.datos, oscuro]);

  const escribir = async (e) => {
    e.preventDefault();
    if (!texto.trim() || enviando) return;
    setEnviando(true);
    setAviso(null);
    try {
      const { data } = await api.post(`/forms/publico/${token}/seguimiento`, { t, texto, trampa }, { skipAuthRedirect: true });
      setEstado({ cargando: false, datos: data, error: null });
      setTexto('');
      setAviso('Tu mensaje llegó al equipo.');
    } catch (err) {
      setAviso(mensajeDe(err, 'No se pudo enviar. Intenta de nuevo en unos minutos.'));
    } finally {
      setEnviando(false);
    }
  };

  const d = estado.datos;
  return (
    <div className="fm-raiz fr-pagina fm-scroll" style={vars}>
      <div className="fm-lienzo fr-lienzo">
        <div className="fm-columna">
          {estado.cargando && <div className="fr-cargando"><Loader2 className="h-9 w-9 od-spin" aria-label="Cargando" /></div>}
          {estado.error && (
            <div className="fm-tarjeta fm-tarjeta-encabezado fr-aviso">
              <h1 className="fm-titulo-form">Seguimiento de tu caso</h1>
              <p>{estado.error}</p>
            </div>
          )}
          {d && (
            <>
              <div className="fm-tarjeta fm-tarjeta-encabezado">
                <h1 className="fm-titulo-form">{d.titulo}</h1>
                <div className="bz-seg-cabecera">
                  <div>
                    <span className="bz-seg-rotulo">Folio</span>
                    <span className="bz-seg-folio">{d.folio}</span>
                  </div>
                  <div>
                    <span className="bz-seg-rotulo">Estado</span>
                    <span className={`bz-estado is-${d.estado.id}`}>{d.estado.nombre}</span>
                  </div>
                  {d.categoria && (
                    <div>
                      <span className="bz-seg-rotulo">Tipo</span>
                      <span>{d.categoria}</span>
                    </div>
                  )}
                </div>
                <p className="fm-texto-2 text-[13px]">Guarda este enlace: con él puedes volver a consultar tu caso cuando quieras.</p>
              </div>

              <div className="fm-tarjeta">
                <ol className="bz-seg-historial">
                  {d.historial.map((h, i) => (
                    <li key={i} className={`bz-seg-evento is-${h.tipo} is-de-${h.de}`}>
                      {h.tipo === 'respuesta' || h.tipo === 'ciudadano' ? (
                        <div className="bz-seg-burbuja">
                          <span className="bz-seg-quien">{h.de === 'tu' ? 'Tú' : 'Equipo de atención'}</span>
                          <p>{h.texto}</p>
                          <time dateTime={h.fecha}>{fechaHora(h.fecha)}</time>
                        </div>
                      ) : (
                        <div className="bz-seg-hito">
                          <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                          <span>{h.texto}</span>
                          <time dateTime={h.fecha}>{fechaHora(h.fecha)}</time>
                        </div>
                      )}
                    </li>
                  ))}
                </ol>
              </div>

              <form className="fm-tarjeta bz-seg-escribir" onSubmit={escribir}>
                <label htmlFor="bz-seg-texto" className="fm-titulo-pregunta">
                  {d.estado.abierto ? '¿Quieres añadir algo?' : '¿El problema sigue? Escríbenos y reabrimos tu caso'}
                </label>
                <textarea id="bz-seg-texto" rows={4} maxLength={3000} value={texto} onChange={(e) => setTexto(e.target.value)} placeholder="Tu mensaje" />
                {/* Campo trampa: invisible para las personas, los robots lo llenan */}
                <input type="text" tabIndex={-1} autoComplete="off" value={trampa} onChange={(e) => setTrampa(e.target.value)} className="fr-trampa" aria-hidden="true" />
                <div className="flex items-center gap-3">
                  <button type="submit" className="fm-boton" disabled={!texto.trim() || enviando}>
                    {enviando ? <Loader2 className="h-4 w-4 od-spin" /> : <Send className="h-4 w-4" />} Enviar
                  </button>
                  {aviso && <span className="fm-texto-2 text-[13px]" role="status">{aviso}</span>}
                </div>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
